import type { PartialDeep } from 'type-fest';

import { IStock } from '@notino/shared/definitions/custom-definitions';
import {
  AvailabilityState,
  GetCatalogProductViewQuery,
  GetProductsByIdBatchedQuery,
  VtoProductBasic,
} from '@notino/shared/definitions/types';
import {
  AdditionalServicesAvailability,
  getVariantPriceWithoutTax,
  IProductPrice,
} from '@notino/web-tracking';
import { Product } from '@notino/web-tracking/dist/types/package/ga4/events/products';

import { IConditionalVoucherDiscount } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/ConditionalVoucherDiscount';
import { getModifaceAvailability } from '@containers/ProductDetailContainer/ProductDetail/utils';
import { getGiftCampaigns } from '@lib/gift';

import { IVariantAddToCart } from './model';
import { getPromoLabels } from './utils';

const getAvailability = (
  variant: Pick<
    IVariantAddToCart,
    'showWatchdog' | 'stockAvailability' | 'availability'
  >
): Product['availability'] => {
  if (
    variant.showWatchdog ||
    variant.availability?.state === AvailabilityState.ShowWatchdog
  ) {
    return 'watchdog';
  }
  return (
    variant.stockAvailability?.code?.toLowerCase() !==
    IStock.outOfStock.toLowerCase()
  );
};

interface IProductEventWither<
  ProductEventType extends Record<string, unknown>
> {
  withProduct: (
    product: ProductEventWitherProduct
  ) => IProductEventWither<
    ProductEventType &
      Pick<
        Product,
        | 'collection'
        | 'rating'
        | 'brand_name'
        | 'brand_id'
        | 'manufacturer_name'
        | 'primary_category'
        | 'primary_subcategory'
        | 'primary_type'
        | 'kind'
        | 'group'
      >
  >;
  withPartialProduct: (
    product: Partial<ProductEventWitherProduct>
  ) => IProductEventWither<
    ProductEventType &
      Pick<
        Product,
        | 'collection'
        | 'rating'
        | 'brand_name'
        | 'brand_id'
        | 'manufacturer_name'
        | 'primary_category'
        | 'primary_subcategory'
        | 'primary_type'
        | 'kind'
        | 'group'
      >
  >;
  withVpProduct: (
    product: Pick<
      NonNullable<GetProductsByIdBatchedQuery['vpProductByIds'][number]>,
      | 'brand'
      | 'productCode'
      | 'name'
      | 'wishlisted'
      | 'attributes'
      | 'primaryCategories'
    > &
      IVariantAddToCart
  ) => IProductEventWither<
    ProductEventType &
      Pick<
        Product,
        | 'rating'
        | 'brand_name'
        | 'primary_category'
        | 'primary_subcategory'
        | 'primary_type'
        | 'kind'
        | 'group'
        | 'product_code'
        | 'product_name'
        | 'favorite'
        | 'promo_labels'
        | 'availability'
        | 'price'
        | 'full_price'
      >
  >;
  withVtoProduct: (
    product?: VtoProductBasic
  ) => IProductEventWither<
    ProductEventType &
      Pick<
        Product,
        | 'brand_name'
        | 'product_code'
        | 'product_name'
        | 'favorite'
        | 'availability'
        | 'price'
        | 'full_price'
      >
  >;
  withVariant: (
    variant: NonNullable<
      GetCatalogProductViewQuery['productDetailByCatalogMasterId']
    >['variants'][number]
  ) => IProductEventWither<
    ProductEventType &
      Pick<
        Product,
        | 'product_code'
        | 'product_name'
        | 'favorite'
        | 'product_gender'
        | 'promo_labels'
        | 'availability'
        | 'price'
        | 'full_price'
      >
  >;
  withDiscountPrices: (
    attributes: NonNullable<
      GetCatalogProductViewQuery['productDetailByCatalogMasterId']
    >['variants'][number]['attributes'],
    options?: { shouldSendOnlyDiscountPrice?: boolean }
  ) => IProductEventWither<
    ProductEventType &
      Pick<
        Product,
        | 'product_code'
        | 'product_name'
        | 'favorite'
        | 'product_gender'
        | 'promo_labels'
        | 'availability'
        | 'price'
        | 'full_price'
      >
  >;
  withPartialVariant: (
    variant: PartialDeep<
      NonNullable<
        GetCatalogProductViewQuery['productDetailByCatalogMasterId']
      >['variants'][number]
    >
  ) => IProductEventWither<
    ProductEventType &
      Pick<
        Product,
        | 'product_code'
        | 'product_name'
        | 'favorite'
        | 'product_gender'
        | 'promo_labels'
        | 'availability'
        | 'price'
        | 'full_price'
      >
  >;
  withServices: (services?: {
    tryItFirstAvailable: AdditionalServicesAvailability;
    engravingAvailable: AdditionalServicesAvailability;
    discoveryBoxAvailable: boolean;
    skinAnalyzer?: boolean;
    hairAnalyzer?: boolean;
  }) => IProductEventWither<ProductEventType & Pick<Product, 'services'>>;
  withAdditionalData: <T extends Partial<Product>>(
    additionalData: T
  ) => IProductEventWither<ProductEventType & T>;
  withAdditionalPromoLabels: (
    promoLabels: string[]
  ) => IProductEventWither<ProductEventType>;
  build: () => ProductEventType;
  forceBuild: () => Product;
}

const getServices = (
  product: ProductEventWitherProduct | null,
  variant: ProductEventWitherProduct['variants'][number] | null,
  {
    tryItFirstAvailable,
    engravingAvailable,
    discoveryBoxAvailable,
    skinAnalyzer,
    hairAnalyzer,
  }: NonNullable<
    Parameters<IProductEventWither<Record<string, unknown>>['withServices']>[0]
  >
) => {
  const services: string[] = [];

  const {
    trackingAttrs: { giftAvailable, moreGifts },
  } =
    product && variant
      ? getGiftCampaigns({ product, variant })
      : { trackingAttrs: { giftAvailable: false, moreGifts: false } };

  if (engravingAvailable === AdditionalServicesAvailability.available) {
    services.push('engraving');
  }
  if (tryItFirstAvailable === AdditionalServicesAvailability.available) {
    services.push('try_it_first');
  }
  if (variant?.features?.versionedModifaceEffects) {
    services.push(`modiface_${getModifaceAvailability(true)}`);
  }
  if ((variant?.availability.availablePickUpStores ?? 0) > 0) {
    services.push('click_and_collect');
  }
  if (giftAvailable) {
    services.push('gift_available');
  }
  if (moreGifts) {
    services.push('more_gifts');
  }
  if (
    (product?.features?.availableSets?.sets?.length ?? 0) > 0 ||
    (variant?.features?.availableSets?.sets?.length ?? 0) > 0
  ) {
    services.push('gift_sets');
  }
  if ((product?.features?.accessories?.products?.length ?? 0) > 0) {
    services.push('accessories');
  }
  if (product?.features?.isShadeFinderAvailable) {
    services.push('shadefinder_available');
  }
  if (discoveryBoxAvailable) {
    services.push('discovery_box');
  }
  if (skinAnalyzer) {
    services.push('skin_anl');
  }
  if (hairAnalyzer) {
    services.push('hair_anl');
  }
  return services;
};

type ProductBase = NonNullable<
  GetCatalogProductViewQuery['productDetailByCatalogMasterId']
>;

export type ProductEventWitherProduct = Omit<ProductBase, 'brand'> & {
  brand: Pick<
    NonNullable<ProductBase['brand']>,
    'name' | 'id' | 'manufacturer'
  > | null;
};

export const ProductEventWither = <
  ProductEventType extends Record<string, unknown>
>(): IProductEventWither<ProductEventType> => {
  let event: ProductEventType = {} as ProductEventType;
  let variant: ProductBase['variants'][number] | null = null;
  let product: ProductEventWitherProduct | null = null;

  return {
    withProduct(p) {
      if (!p) {
        return this;
      }
      product = p;
      event = {
        ...event,
        collection: product.collection,
        rating: String(product.reviewOverview?.rating ?? ''),
        brand_name: product.brand?.name,
        brand_id: product.brand?.id,
        manufacturer_name: product.brand?.manufacturer,
        primary_category: product.primaryCategories?.category?.name,
        primary_subcategory: product.primaryCategories?.subCategory?.name,
        primary_type: product.primaryCategories?.type?.name,
        kind: product.primaryCategories?.kind?.name,
        group: undefined, // not returned from api yet,
      };
      return this;
    },
    withVariant(v) {
      if (!v) {
        return this;
      }
      variant = v;

      event = {
        ...event,
        product_code: variant.productCode,
        product_name: `${variant.name ?? ''} ${
          variant.variantName || product?.annotation || ''
        } ${variant.additionalInfo ?? ''}`.trim(),
        favorite: variant.wishlisted,
        product_gender: variant.gender,
        promo_labels: getPromoLabels(variant.attributes),
        availability: getAvailability(
          variant as unknown as Pick<
            IVariantAddToCart,
            'showWatchdog' | 'availability' | 'stockAvailability'
          >
        ),
        price: variant
          ? getVariantPriceWithoutTax(variant.price as IProductPrice)
          : undefined,
        full_price: variant.price?.value,
      };
      return this;
    },
    withDiscountPrices(
      attributes,
      options = { shouldSendOnlyDiscountPrice: false }
    ) {
      if (!attributes) {
        return this;
      }

      const { ConditionalVoucherDiscount, VoucherDiscount } = attributes;

      if (VoucherDiscount) {
        event = {
          ...event,
          discount_price: VoucherDiscount.discountedPrice,
          ...(!options.shouldSendOnlyDiscountPrice && {
            percentage_discount: VoucherDiscount.percentage,
            percentage_discount_min_price:
              VoucherDiscount.percentageFromMinimal,
          }),
        };

        return this;
      }

      const discountConditions = ConditionalVoucherDiscount?.discountConditions;

      if (discountConditions?.length === 1) {
        const { discountedPrice, percentageDiscount, percentageFromMinimal } =
          discountConditions[0] as IConditionalVoucherDiscount['discountConditions'][number];

        event = {
          ...event,
          discount_price: discountedPrice,
          ...(!options.shouldSendOnlyDiscountPrice && {
            percentage_discount: percentageDiscount,
            percentage_discount_min_price: percentageFromMinimal,
          }),
        };
      }

      return this;
    },
    withPartialProduct(p) {
      return this.withProduct(p);
    },
    withVpProduct(p) {
      if (!p) {
        return this;
      }
      event = {
        ...event,
        rating: p.reviewOverview?.rating,
        brand_name: p.brand?.name,
        primary_category: p.primaryCategories?.category,
        primary_subcategory: p.primaryCategories?.subCategory,
        primary_type: p.primaryCategories?.type,
        kind: p.primaryCategories?.kind,
        group: undefined, // not returned from api yet,
        product_code: p.productCode,
        product_name: `${p.name ?? ''} ${p.annotation ?? ''}`.trim(),
        favorite: p.wishlisted,
        promo_labels: getPromoLabels(p.attributes),
        availability: getAvailability(p),
        price: p ? getVariantPriceWithoutTax(p.price) : undefined,
        full_price: p.price?.value,
      };
      return this;
    },
    withVtoProduct(p) {
      if (!p) {
        return this;
      }
      event = {
        ...event,
        brand_name: p.brandName,
        product_code: p.productCode,
        product_name: `${p.name ?? ''} ${p.annotation ?? ''} ${
          p.additionalInfo ?? ''
        }`.trim(),
        favorite: p.wishlisted,
        availability: p.stockAvailability === 'OutOfStock' ? false : true,
        price: p ? getVariantPriceWithoutTax(p.price) : undefined,
        full_price: p.price?.value,
      };
      return this;
    },
    withPartialVariant(v) {
      return this.withVariant(v as ProductBase['variants'][number]);
    },
    withServices(services) {
      if (!services) {
        return this;
      }
      event = {
        ...event,
        services: getServices(product, variant, services),
      };
      return this;
    },
    withAdditionalData(additionalData) {
      event = {
        ...event,
        ...additionalData,
      };
      return this;
    },
    withAdditionalPromoLabels(promoLabels) {
      event = {
        ...event,
        promo_labels: [
          ...((event.promo_labels ?? []) as string[]),
          ...promoLabels.filter(Boolean),
        ],
      };
      return this;
    },
    build() {
      return event;
    },
    forceBuild() {
      return event as unknown as Product;
    },
  };
};
